import type { SituationCardList } from "@peer-start/platform-contracts";
import { Button, Card, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@peer-start/ui/reducate";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { SituationChangedBy } from "./SituationChangedBy.js";
import { SituationGroundednessMeter } from "./SituationGroundedness.js";
import { SituationStatus } from "./SituationStatus.js";

type SituationCardRow = SituationCardList["items"][number];

export function SituationCardTable({
  cards,
  canEdit,
  onOpen,
  onPage,
  page,
  pages,
}: {
  cards: readonly SituationCardRow[];
  canEdit: (card: SituationCardRow) => boolean;
  onOpen: (card: SituationCardRow) => void;
  onPage: (page: number) => void;
  page: number;
  pages: number;
}) {
  const { t, i18n } = useTranslation();
  return (
    <>
      <Card className="py-0" padding="none">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("administration.situationCard.columnTitle")}</TableHead>
              <TableHead>{t("administration.situationCard.columnSummary")}</TableHead>
              <TableHead>{t("administration.situationCard.status")}</TableHead>
              <TableHead>{t("administration.situationCard.groundedness")}</TableHead>
              <TableHead>{t("administration.situationCard.columnChanged")}</TableHead>
              <TableHead>{t("administration.situationCard.columnChangedBy")}</TableHead>
              <TableHead>
                <span className="sr-only">{t("administration.situationCard.edit")}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cards.map((card) => (
              <TableRow className="group/row cursor-pointer" key={card.id} onClick={() => onOpen(card)}>
                <TableCell className="font-medium whitespace-nowrap">{card.title}</TableCell>
                <TableCell className="max-w-md whitespace-normal text-sm leading-snug text-muted-foreground">
                  {t(`administration.situationCard.cardSummaries.${card.id}`, { defaultValue: card.summary })}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <SituationStatus status={card.status} />
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <SituationGroundednessMeter percent={card.groundedness} />
                </TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  <time dateTime={card.changedAt}>
                    {new Intl.DateTimeFormat(i18n.language, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    }).format(new Date(card.changedAt))}
                  </time>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <SituationChangedBy id={card.changedBy.id} name={card.changedBy.name} />
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button
                    asChild
                    className="opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100"
                    controlId={`situation.list.open.${card.id}`}
                    variant="link"
                  >
                    <NavLink
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onOpen(card);
                      }}
                      to={`/admin/situation-cards/${card.id}`}
                    >
                      {t(canEdit(card) ? "administration.situationCard.edit" : "administration.situationCard.view")}
                    </NavLink>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <div className="mt-3 flex items-center justify-end gap-2">
        <Button
          controlId="situation.list.previous"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          type="button"
          variant="secondary"
        >
          {t("administration.situationCard.previous")}
        </Button>
        <span className="text-sm text-muted-foreground">
          {t("administration.situationCard.page", { page, pages })}
        </span>
        <Button
          controlId="situation.list.next"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          type="button"
          variant="secondary"
        >
          {t("administration.situationCard.next")}
        </Button>
      </div>
    </>
  );
}
